import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { toast } from 'sonner';
import MedicineSetupStep from '../components/MedicineSetupStep';
import MedicineTray from '../components/MedicineTray';
import SetupWizard from '../components/SetupWizard';
import { Sidebar, LinkTab, MedicinesTab, SettingsTab, GestioneVisiteTab, ScudoTab } from '../components/Dashboard';
import RichiestaRicettaButton from '../components/RichiestaRicetta';
import NotificationToggle from '../components/NotificationToggle';
import StazioneDigitaleCard from '../components/StazioneDigitaleCard';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import DesktopBlocker, { useDesktopDetection } from '../components/DesktopBlocker';
import PlanSelectionModal from '../components/PlanSelectionModal';
import { PlanCheckFailedWall } from '../components/PlanCheckFailedWall';
import { entryMonthlyPrice, purchasablePlanNames } from '../lib/pricingPlans';
import { useFounderSpots } from '../hooks/useFounderSpots';
import PrivacyConsentModal from '../components/PrivacyConsentModal';
import { WalkStatusCard } from '../components/WalkTracking';
import { LockedGPSCard } from '../components/LockedFeatureCard';
import OnboardingSteps from '../components/Dashboard/OnboardingSteps';
import { calcolaEta } from '../utils/ageUtils';
import { consumePortalRequest, isPaidPlan, openStripePortal } from '../lib/stripePortal';
import { 
  Clock, 
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  Heart,
  Pill,
  Utensils,
  FileText,
  Camera,
  Copy,
  CheckCircle2,
  MessageCircle,
  TrendingUp,
  Smile,
  Meh,
  Frown,
  RefreshCw,
  Menu,
  X,
  Link2,
  Crown,
  Star,
  Zap,
  Activity,
  Image,
  ChevronLeft,
  ChevronRight,
  Flame,
  Sun,
  Sparkles,
  Plus,
  Trash2,
  Download,
  FileDown,
  Loader2,
  Lock,
  Bell,
  AlertTriangle,
  Users,
  ArrowRight,
  CreditCard,
  Edit3,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { format, formatDistanceToNow, subDays, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { generatePDFReport, calculateStats } from '../utils/pdfReportGenerator';
import { fetchReportModel } from '../utils/reportData';
import MedicoSettings from '../components/MedicoSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo Component
const Logo = ({ className = "h-14" }) => (
  <img 
    src="/logo-curacheck.png" 
    alt="CuraCheck" 
    className={`${className} object-contain`}
  />
);

// Subscription Status Badge Component
const SubscriptionBadge = ({ subscriptionStatus }) => {
  if (!subscriptionStatus || !subscriptionStatus.display_badge) return null;
  
  const { display_badge } = subscriptionStatus;
  
  const colorClasses = {
    success: 'bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]',
    warning: 'bg-[#FFF3E0] text-[#E65100] border-[#FFCC80]',
    danger: 'bg-[#FFEBEE] text-[#C62828] border-[#EF9A9A]',
    muted: 'bg-[#F5F5F5] text-[#616161] border-[#E0E0E0]'
  };
  
  const iconMap = {
    check: <CheckCircle2 className="w-4 h-4" />,
    clock: <Clock className="w-4 h-4" />,
    alert: <AlertTriangle className="w-4 h-4" />,
    x: <X className="w-4 h-4" />,
    user: <Users className="w-4 h-4" />
  };
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium ${colorClasses[display_badge.color] || colorClasses.muted}`}>
      {iconMap[display_badge.icon] || iconMap.user}
      <span>{display_badge.text}</span>
    </div>
  );
};

// Activity Icon Component
const ActivityIcon = ({ type, className = "w-5 h-5" }) => {
  const icons = {
    medicine: <Pill className={className} />,
    meal: <Utensils className={className} />,
    mood: <Heart className={className} />,
    note: <FileText className={className} />
  };
  return icons[type] || <Activity className={className} />;
};

// Mood Icon Component
const MoodIcon = ({ mood, size = "w-6 h-6" }) => {
  if (mood === 'happy') return <Smile className={`${size} text-[#4CAF50]`} />;
  if (mood === 'neutral') return <Meh className={`${size} text-[#FFC107]`} />;
  if (mood === 'sad') return <Frown className={`${size} text-[#F44336]`} />;
  return <Meh className={`${size} text-gray-400`} />;
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subtext, gradient }) => (
  <div className={`relative overflow-hidden rounded-2xl p-5 ${gradient} text-white`}>
    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10" />
    <Icon className="w-8 h-8 mb-3 opacity-90" />
    <p className="text-3xl font-bold">{value}</p>
    <p className="text-sm opacity-80">{label}</p>
    {subtext && <p className="text-xs opacity-60 mt-1">{subtext}</p>}
  </div>
);

// Notification Status Card Component (for Standard users)
const NotificationStatusCard = ({ planData, onUpgrade }) => {
  if (!planData) return null;
  
  const { plan, notifications, trial } = planData;
  
  // Premium users: don't show limit (unlimited)
  if (plan === 'premium') {
    return (
      <div className="bg-gradient-to-br from-[#F59E0B]/10 to-[#EF4444]/10 rounded-2xl p-4 border border-[#F59E0B]/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#F59E0B] to-[#EF4444] rounded-xl flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-[#2C3E50]">Piano Premium</p>
            <p className="text-sm text-[#64748B]">Notifiche illimitate • 2 destinatari</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Fondatori users
  if (plan === 'fondatori' || plan === 'founder') {
    return (
      <div className="bg-gradient-to-br from-[#10B981]/10 to-[#059669]/10 rounded-2xl p-4 border border-[#10B981]/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-xl flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-[#2C3E50]">Founder Club</p>
            <p className="text-sm text-[#64748B]">Grazie per il tuo supporto!</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Standard users
  if (plan === 'standard') {
    return (
      <div className="bg-gradient-to-br from-[#3B82F6]/10 to-[#2563EB]/10 rounded-2xl p-4 border border-[#3B82F6]/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#3B82F6] to-[#2563EB] rounded-xl flex items-center justify-center">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-[#2C3E50]">Piano Standard</p>
            <p className="text-sm text-[#64748B]">
              {notifications ? `${notifications.remaining}/${notifications.limit} notifiche oggi` : '10 notifiche/giorno'}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Trial users: show trial status
  if (trial?.is_trial) {
    const isExpired = trial.expired;
    const hoursRemaining = trial.hours_remaining || 0;
    
    if (isExpired) {
      return (
        <div className="bg-gradient-to-br from-[#EF4444]/10 to-[#EF4444]/20 rounded-2xl p-4 border border-[#EF4444]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#EF4444] rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#EF4444]">Prova scaduta</p>
              <p className="text-sm text-[#64748B]">Effettua l'upgrade per continuare a usare CuraCheck</p>
            </div>
          </div>
          <Button
            onClick={onUpgrade}
            className="w-full mt-3 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-xl"
          >
            Attiva un piano
          </Button>
        </div>
      );
    }
    
    return (
      <div className="bg-gradient-to-br from-[#10B981]/10 to-[#10B981]/5 rounded-2xl p-4 border border-[#10B981]/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#10B981] rounded-xl flex items-center justify-center">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-[#2C3E50]">Prova Gratuita</p>
            {trial.end_date && (
              <p className="text-sm text-[#64748B]" data-testid="trial-time-remaining">
                {hoursRemaining > 24 
                  ? `${Math.ceil(hoursRemaining / 24)} giorni rimanenti` 
                  : `${hoursRemaining} ore rimanenti`}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Standard users: show notification counter
  if (plan === 'standard' && notifications) {
    const { sent_today, limit, remaining } = notifications;
    const percentage = limit > 0 ? Math.round((sent_today / limit) * 100) : 0;
    const isLow = remaining <= 2;
    
    return (
      <div className={`rounded-2xl p-4 border ${isLow ? 'bg-[#FEF3C7] border-[#F59E0B]/30' : 'bg-[#E8F5E9] border-[#4A7C59]/20'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isLow ? 'bg-[#F59E0B]' : 'bg-[#4A7C59]'}`}>
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-[#2C3E50]">Notifiche oggi</p>
              <p className="text-sm text-[#64748B]">Piano Standard</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold ${isLow ? 'text-[#F59E0B]' : 'text-[#4A7C59]'}`}>
              {remaining}
            </p>
            <p className="text-xs text-[#9CA3AF]">rimanenti</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-white/80 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${isLow ? 'bg-[#F59E0B]' : 'bg-[#4A7C59]'}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <p className="text-xs text-[#64748B] mt-2 text-center">
          {sent_today} di {limit} notifiche utilizzate oggi
        </p>
        
        {isLow && (
          <Button
            onClick={onUpgrade}
            className="w-full mt-3 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] hover:from-[#D97706] hover:to-[#DC2626] text-white rounded-xl text-sm"
          >
            <Crown className="w-4 h-4 mr-2" />
            Passa a Premium per notifiche illimitate
          </Button>
        )}
      </div>
    );
  }
  
  return null;
};

// SetupWizard component moved to /components/SetupWizard/index.jsx
// Timeline Item Component
const TimelineItem = ({ item, isLast }) => {
  const getActivityLabel = (type, detail, statusLabel, statusEmoji) => {
    // Se è uno stato con label estesa, usa quella
    if (type === 'mood' && statusLabel) {
      return statusLabel;
    }
    
    const labels = {
      medicine: { morning: 'Routine mattina', afternoon: 'Routine pomeriggio', evening: 'Routine sera' },
      meal: { breakfast: 'Colazione', lunch: 'Pranzo', dinner: 'Cena' },
      mood: { happy: 'Umore felice', neutral: 'Umore neutro', sad: 'Umore triste' },
      note: { default: 'Nota' },
      wellness: { 
        igiene: 'Igiene personale', 
        passeggiata: 'Passeggiata', 
        riposo: 'Riposo', 
        esercizi: 'Esercizi',
        altro: 'Attività'
      }
    };
    return labels[type]?.[detail] || detail || type;
  };

  const getActivityColor = (type, statusCategoria) => {
    // Colori specifici per categorie di stato
    if (type === 'mood' && statusCategoria) {
      const statusColors = {
        fisico: 'bg-[#E3F2FD] text-[#1976D2] border-[#BBDEFB]',
        appetito: 'bg-[#FFF8E1] text-[#FF8F00] border-[#FFE082]',
        umore: 'bg-[#FCE4EC] text-[#C2185B] border-[#F8BBD9]'
      };
      return statusColors[statusCategoria] || 'bg-[#FCE4EC] text-[#C2185B] border-[#F8BBD9]';
    }
    
    const colors = {
      medicine: 'bg-[#E3F2FD] text-[#1976D2] border-[#BBDEFB]',
      meal: 'bg-[#FFF3E0] text-[#E65100] border-[#FFE0B2]',
      mood: 'bg-[#FCE4EC] text-[#C2185B] border-[#F8BBD9]',
      note: 'bg-[#F3E5F5] text-[#7B1FA2] border-[#E1BEE7]',
      wellness: 'bg-[#E8F8F5] text-[#117A65] border-[#A3E4D7]'
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  // Ottieni categoria label per il badge
  const getCategoriaLabel = (categoria) => {
    const labels = {
      fisico: '💪 Fisico',
      appetito: '🍽️ Appetito',
      umore: '❤️ Umore'
    };
    return labels[categoria] || null;
  };

  const timeAgo = formatDistanceToNow(new Date(item.timestamp), { addSuffix: true, locale: it });

  return (
    <div className="flex gap-4">
      {/* Timeline line */}
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 rounded-full ${getActivityColor(item.type, item.status_categoria).split(' ')[0]} flex items-center justify-center border-2 ${getActivityColor(item.type, item.status_categoria).split(' ')[2]}`}>
          {/* Se c'è un'emoji dello stato, mostrala invece dell'icona */}
          {item.status_emoji ? (
            <span className="text-lg">{item.status_emoji}</span>
          ) : (
            <ActivityIcon type={item.type} className="w-5 h-5" />
          )}
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-gradient-to-b from-[#E5E7EB] to-transparent min-h-[40px]" />}
      </div>

      {/* Content */}
      <div className={`flex-1 pb-6 ${isLast ? '' : ''}`}>
        <div className="bg-white rounded-xl p-4 border border-[#E5E7EB] shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              {/* Badge categoria per stati estesi */}
              {item.status_categoria && (
                <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-[#F0F4F1] text-[#64748B] mb-1">
                  {getCategoriaLabel(item.status_categoria)}
                </span>
              )}
              
              <p className="font-semibold text-[#2C3E50] flex items-center gap-2">
                {/* Emoji grande per stati */}
                {item.status_emoji && <span className="text-xl">{item.status_emoji}</span>}
                {getActivityLabel(item.type, item.detail, item.status_label, item.status_emoji)}
              </p>
              
              {item.medicine_name && (
                <p className="text-sm text-[#4A7C59] mt-1">
                  <Pill className="w-3 h-3 inline mr-1" />
                  {item.medicine_name}
                </p>
              )}
              {item.note_text && (
                <p className="text-sm text-[#64748B] mt-1 italic">"{item.note_text}"</p>
              )}
            </div>
            <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{timeAgo}</span>
          </div>
          
          {/* Miniatura Foto con Lazy Loading e Lightbox */}
          {item.photo_url && (
            <div 
              className="mt-3 cursor-pointer group"
              onClick={() => {
                // Costruisci URL completo per la foto
                const fullPhotoUrl = item.photo_url.startsWith('http') ? item.photo_url : `${API_URL}${item.photo_url}`;
                // Apri lightbox
                const lightbox = document.createElement('div');
                lightbox.className = 'fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4';
                lightbox.onclick = () => lightbox.remove();
                lightbox.innerHTML = `
                  <button class="absolute top-4 right-4 text-white hover:text-white/80 p-2">
                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                  <img src="${fullPhotoUrl}" alt="Foto" class="max-w-full max-h-full object-contain rounded-lg" />
                `;
                document.body.appendChild(lightbox);
              }}
            >
              <img 
                src={item.photo_url.startsWith('http') ? item.photo_url : `${API_URL}${item.photo_url}`} 
                alt="Foto attività" 
                loading="lazy"
                className="w-20 h-20 object-cover rounded-lg border-2 border-[#E5E7EB] group-hover:border-[#4A7C59] transition-all shadow-sm"
              />
              <p className="text-xs text-[#9CA3AF] mt-1 flex items-center gap-1">
                <Camera className="w-3 h-3" />
                Tocca per ingrandire
              </p>
            </div>
          )}

          {item.whatsapp_sent && (
            <div className="flex items-center gap-1 mt-2 text-xs text-[#25D366]">
              <CheckCircle2 className="w-3 h-3" />
              <span>Notifica inviata</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Photo Gallery Component
const PhotoGallery = ({ photos, loading }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Helper per costruire URL completo
  const getFullUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-square bg-[#F0F4F1] rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-12 bg-[#FAF9F6] rounded-2xl">
        <Camera className="w-12 h-12 mx-auto text-[#C8D6C5] mb-3" />
        <p className="text-[#64748B]">Nessuna foto dei pasti ancora</p>
        <p className="text-sm text-[#9CA3AF]">Le foto appariranno qui quando l'assistente le caricherà</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((photo) => (
          <div
            key={photo.id}
            onClick={() => setSelectedPhoto(photo)}
            className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer border-2 border-transparent hover:border-[#4A7C59] transition-all"
          >
            <img 
              src={getFullUrl(photo.url)} 
              alt="Pasto" 
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="absolute bottom-2 left-2 right-2">
                <p className="text-white text-sm font-medium capitalize">{photo.meal_type}</p>
                <p className="text-white/70 text-xs">{format(new Date(photo.timestamp), 'd MMM', { locale: it })}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-white/80"
            onClick={() => setSelectedPhoto(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={getFullUrl(selectedPhoto.url)} 
            alt="Pasto" 
            className="max-w-full max-h-[80vh] rounded-lg"
          />
          <div className="absolute bottom-8 text-center text-white">
            <p className="text-lg font-medium capitalize">{selectedPhoto.meal_type}</p>
            <p className="text-white/70">{format(new Date(selectedPhoto.timestamp), 'EEEE d MMMM yyyy, HH:mm', { locale: it })}</p>
          </div>
        </div>
      )}
    </>
  );
};

// Mood Chart Component (Visual)
const MoodChart = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-32 bg-[#F0F4F1] rounded-xl" />
        <div className="h-20 bg-[#F0F4F1] rounded-xl" />
      </div>
    );
  }

  if (!stats || stats.total === 0) {
    return (
      <div className="text-center py-12 bg-[#FAF9F6] rounded-2xl">
        <Heart className="w-12 h-12 mx-auto text-[#C8D6C5] mb-3" />
        <p className="text-[#64748B]">Nessun dato sull'umore ancora</p>
        <p className="text-sm text-[#9CA3AF]">I dati appariranno qui quando verranno registrati</p>
      </div>
    );
  }

  const moodColors = {
    happy: { bg: 'bg-[#E8F5E9]', bar: 'bg-[#4CAF50]', text: 'text-[#2E7D32]' },
    neutral: { bg: 'bg-[#FFF8E1]', bar: 'bg-[#FFC107]', text: 'text-[#F57F17]' },
    sad: { bg: 'bg-[#FFEBEE]', bar: 'bg-[#F44336]', text: 'text-[#C62828]' }
  };

  return (
    <div className="space-y-6">
      {/* Donut-style visual */}
      <div className="flex items-center justify-center gap-8 py-6">
        <div className="relative w-36 h-36">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#F0F4F1" strokeWidth="12" />
            {/* Happy segment */}
            <circle 
              cx="50" cy="50" r="40" fill="none" 
              stroke="#4CAF50" strokeWidth="12"
              strokeDasharray={`${stats.percentages.happy * 2.51} 251`}
              strokeDashoffset="0"
              className="transition-all duration-1000"
            />
            {/* Neutral segment */}
            <circle 
              cx="50" cy="50" r="40" fill="none" 
              stroke="#FFC107" strokeWidth="12"
              strokeDasharray={`${stats.percentages.neutral * 2.51} 251`}
              strokeDashoffset={`${-stats.percentages.happy * 2.51}`}
              className="transition-all duration-1000"
            />
            {/* Sad segment */}
            <circle 
              cx="50" cy="50" r="40" fill="none" 
              stroke="#F44336" strokeWidth="12"
              strokeDasharray={`${stats.percentages.sad * 2.51} 251`}
              strokeDashoffset={`${-(stats.percentages.happy + stats.percentages.neutral) * 2.51}`}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center flex-col">
            <p className="text-3xl font-bold text-[#2C3E50]">{stats.total}</p>
            <p className="text-xs text-[#64748B]">registrazioni</p>
          </div>
        </div>

        {/* Legend */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full bg-[#4CAF50]" />
            <div>
              <p className="font-medium text-[#2C3E50]">Felice</p>
              <p className="text-sm text-[#64748B]">{stats.counts.happy} ({stats.percentages.happy}%)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full bg-[#FFC107]" />
            <div>
              <p className="font-medium text-[#2C3E50]">Neutro</p>
              <p className="text-sm text-[#64748B]">{stats.counts.neutral} ({stats.percentages.neutral}%)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full bg-[#F44336]" />
            <div>
              <p className="font-medium text-[#2C3E50]">Triste</p>
              <p className="text-sm text-[#64748B]">{stats.counts.sad} ({stats.percentages.sad}%)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Trend line */}
      {stats.trend && stats.trend.length > 0 && (
        <div className="bg-[#FAF9F6] rounded-xl p-4">
          <p className="text-sm font-medium text-[#64748B] mb-3">Ultimi 7 giorni</p>
          <div className="flex items-end justify-between gap-1">
            {stats.trend.map((day, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <MoodIcon mood={day.mood} size="w-8 h-8" />
                <span className="text-xs text-[#9CA3AF]">
                  {format(new Date(day.date), 'EEE', { locale: it })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mood bars */}
      <div className="space-y-3">
        {['happy', 'neutral', 'sad'].map((mood) => (
          <div key={mood} className={`${moodColors[mood].bg} rounded-xl p-3`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MoodIcon mood={mood} size="w-5 h-5" />
                <span className={`font-medium ${moodColors[mood].text} capitalize`}>
                  {mood === 'happy' ? 'Felice' : mood === 'neutral' ? 'Neutro' : 'Triste'}
                </span>
              </div>
              <span className={`text-sm ${moodColors[mood].text}`}>{stats.percentages[mood]}%</span>
            </div>
            <div className="h-2 bg-white/50 rounded-full overflow-hidden">
              <div 
                className={`h-full ${moodColors[mood].bar} rounded-full transition-all duration-1000`}
                style={{ width: `${stats.percentages[mood]}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Sidebar
// Sidebar, LinkTab, MedicinesTab components moved to /components/Dashboard/
const ReportTab = ({ famiglia, user }) => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('weekly');
  const [generating, setGenerating] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPlan, setUserPlan] = useState({ plan: 'base' });
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);

  const loadPlan = async () => {
    if (!user?.id) {
      setLoadingPlan(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/user/${user.id}/plan`);
      if (response.ok) {
        const data = await response.json();
        setUserPlan(data);
      }
    } catch (error) {
      console.error('Error loading plan:', error);
    } finally {
      setLoadingPlan(false);
    }
  };

  // Check user plan
  useEffect(() => {
    loadPlan();
  }, [user]);

  // Auto-refresh piano quando la tab diventa visibile (dopo pagamento)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) {
        loadPlan();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const isPremium = userPlan.plan === 'premium';
  const hasReportAccess = ['premium', 'standard', 'trial', 'fondatori', 'founder'].includes(userPlan.plan?.toLowerCase());

  useEffect(() => {
    loadActivities();
  }, [famiglia?.id, period]);

  const loadActivities = async () => {
    if (!famiglia?.id) return;
    
    setLoading(true);
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = period === 'monthly' 
        ? subMonths(endDate, 1) 
        : subDays(endDate, 7);

      // Fetch activities from timeline API
      const response = await fetch(
        `${API_URL}/api/dashboard/${famiglia.id}/timeline?days=${period === 'monthly' ? 30 : 7}`
      );
      
      if (response.ok) {
        const data = await response.json();
        // Transform timeline data to activities format for PDF
        const activitiesData = (data.timeline || []).map(item => ({
          action_type: item.type,
          action_detail: item.detail,
          created_at: item.timestamp,
          medicine_name: item.medicine_name,
          note_text: item.note_text,
          mood: item.type === 'mood' ? item.detail : null
        }));
        setActivities(activitiesData);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    
    try {
      const endDate = new Date();
      const days = period === 'monthly' ? 30 : 7;
      const startDate = subDays(endDate, days - 1);
      const model = await fetchReportModel({ famiglia, startDate, endDate });
      await generatePDFReport(model);

      toast.success('Report PDF scaricato!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Errore nella generazione del PDF. Riprova.');
    } finally {
      setGenerating(false);
    }
  };

  const stats = calculateStats(activities);

  // Premium upgrade handler
  const handleUpgrade = () => {
    // Apri il modal di scelta piano invece di andare direttamente a Stripe
    setShowPlanModal(true);
  };

  // Show loading state while checking plan
  if (loadingPlan) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[#4A7C59] animate-spin" />
      </div>
    );
  }

  // Show upgrade prompt for users without report access (base/trial only)
  if (!hasReportAccess) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-[#2C3E50]">Scarica Report PDF</h2>
          <p className="text-sm text-[#64748B] mt-1">
            Genera un report professionale con il riepilogo delle attività
          </p>
        </div>

        {/* Upgrade Lock Card */}
        <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-2xl p-6 border border-[#F59E0B]/30">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-white/80 rounded-full flex items-center justify-center mb-4 shadow-lg">
              <Lock className="w-8 h-8 text-[#F59E0B]" />
            </div>
            <h3 className="text-xl font-bold text-[#92400E] mb-2">Funzionalità a pagamento</h3>
            <p className="text-sm text-[#B45309] mb-4 max-w-md">
              La generazione di report PDF è disponibile a partire dal piano Standard. 
              Upgrade ora per accedere a questa e altre funzionalità esclusive!
            </p>
            
            {/* Features Preview */}
            <div className="bg-white/60 rounded-xl p-4 mb-4 w-full max-w-sm">
              <p className="text-xs font-semibold text-[#92400E] mb-2">Con un piano a pagamento hai:</p>
              <ul className="space-y-1.5 text-left">
                <li className="flex items-center gap-2 text-sm text-[#B45309]">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                  Report PDF settimanale
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B45309]">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                  Fino a 2 destinatari notifiche (Premium)
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B45309]">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                  Notifiche illimitate
                </li>
              </ul>
            </div>

            <Button
              onClick={handleUpgrade}
              className="h-12 px-8 bg-gradient-to-r from-[#4A7C59] to-[#3A6345] hover:from-[#3A6345] hover:to-[#2A5235] text-white rounded-xl font-bold shadow-lg"
            >
              <Crown className="w-5 h-5 mr-2" />
              Scegli un Piano
            </Button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-[#EBF5FB] rounded-2xl p-4 border border-[#5DADE2]/20">
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-[#5DADE2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-[#5DADE2]" />
            </div>
            <div>
              <p className="font-medium text-[#2471A3] text-sm">Cosa include il report?</p>
              <p className="text-sm text-[#5DADE2] mt-1">
                Il PDF contiene: riepilogo statistico, elenco dettagliato di farmaci somministrati, 
                pasti, attività e note con data e ora. Perfetto per condividere con il medico.
              </p>
            </div>
          </div>
        </div>

        {/* Plan Selection Modal */}
        <PlanSelectionModal
          isOpen={showPlanModal}
          onClose={() => setShowPlanModal(false)}
          currentPlan={userPlan.plan}
          userId={user?.id}
          famigliaId={famiglia?.id}
          featureRequested="report"
        />
      </div>
    );
  }

  // User with report access (Standard, Fondatori, Premium) - show full functionality
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#2C3E50]">Scarica Report PDF</h2>
          <p className="text-sm text-[#64748B] mt-1">
            Genera un report professionale con il riepilogo delle attività
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white text-xs font-bold rounded-full">
          <Crown className="w-3 h-3" />
          PREMIUM
        </span>
      </div>

      {/* Period Selector */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <label className="block text-sm font-medium text-[#2C3E50] mb-3">
          Seleziona il periodo
        </label>
        <div className="flex gap-3">
          <button
            onClick={() => setPeriod('weekly')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
              period === 'weekly'
                ? 'bg-[#4A7C59] text-white shadow-lg'
                : 'bg-[#F4F7F5] text-[#64748B] hover:bg-[#E8F5E9]'
            }`}
          >
            <Calendar className="w-5 h-5 mx-auto mb-1" />
            Settimanale
          </button>
          <button
            onClick={() => setPeriod('monthly')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
              period === 'monthly'
                ? 'bg-[#4A7C59] text-white shadow-lg'
                : 'bg-[#F4F7F5] text-[#64748B] hover:bg-[#E8F5E9]'
            }`}
          >
            <BarChart3 className="w-5 h-5 mx-auto mb-1" />
            Mensile
          </button>
        </div>
      </div>

      {/* Preview Stats */}
      <div className="bg-[#F4F7F5] rounded-2xl p-5 border border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-[#2C3E50] mb-4">
          Anteprima contenuto ({period === 'monthly' ? 'ultimi 30 giorni' : 'ultimi 7 giorni'})
        </h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <RefreshCw className="w-6 h-6 text-[#4A7C59] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#27AE60]">{stats.medicines}</p>
              <p className="text-xs text-[#64748B]">Medicine</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#F59E0B]">{stats.meals}</p>
              <p className="text-xs text-[#64748B]">Pasti</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#5DADE2]">{stats.wellness}</p>
              <p className="text-xs text-[#64748B]">Attività</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-[#2C3E50]">{stats.notes}</p>
              <p className="text-xs text-[#64748B]">Note</p>
            </div>
          </div>
        )}
        
        <p className="text-xs text-[#9CA3AF] mt-3 text-center">
          Totale: {stats.total} attività registrate
        </p>
      </div>

      {/* Generate Button */}
      <Button
        onClick={handleGenerateReport}
        data-testid="report-generate-btn"
        disabled={generating || loading || activities.length === 0}
        className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
      >
        {generating ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Generazione in corso...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Scarica Report PDF
          </span>
        )}
      </Button>

      {activities.length === 0 && !loading && (
        <p className="text-center text-sm text-[#9CA3AF]">
          Nessuna attività nel periodo selezionato
        </p>
      )}

      {/* Info Box */}
      <div className="bg-[#EBF5FB] rounded-2xl p-4 border border-[#5DADE2]/20">
        <div className="flex gap-3">
          <div className="w-8 h-8 bg-[#5DADE2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-[#5DADE2]" />
          </div>
          <div>
            <p className="font-medium text-[#2471A3] text-sm">Cosa include il report?</p>
            <p className="text-sm text-[#5DADE2] mt-1">
              Il PDF contiene: riepilogo statistico, elenco dettagliato di farmaci somministrati, 
              pasti, attività e note con data e ora. Perfetto per condividere con il medico.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Tab
// SettingsTab component moved to /components/Dashboard/SettingsTab.jsx
const FamiliaDashboard = () => {
  const navigate = useNavigate();
  const { user, supabase, signOut, loading: authLoading } = useAuth();
  const founder = useFounderSpots();
  
  // Desktop blocker detection
  const { shouldBlock: isDesktopDevice } = useDesktopDetection();
  const [desktopBypassed, setDesktopBypassed] = useState(false);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null); // Stato errore per UI fallback
  const [editMode, setEditMode] = useState(false); // Modalità modifica configurazione
  const [showPWAPrompt, setShowPWAPrompt] = useState(false); // PWA install prompt
  
  // Check if running as PWA on mount
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true
      || document.referrer.includes('android-app://');
    
    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    
    if (!isStandalone && !dismissed) {
      setShowPWAPrompt(true);
    }
  }, []);
  
  // Timeout sicurezza - forza fine loading dopo 15 secondi
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.log('[Dashboard] TIMEOUT: Forzo fine loading dopo 15s');
        setLoading(false);
      }
    }, 15000);
    return () => clearTimeout(timeout);
  }, []);
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [moodStats, setMoodStats] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [localFamiglia, setLocalFamiglia] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  
  // Multi-Assistito states
  const [assistiti, setAssistiti] = useState([]);
  const [showAddAssistitoModal, setShowAddAssistitoModal] = useState(false);
  const [newAssistitoName, setNewAssistitoName] = useState('');
  const [addingAssistito, setAddingAssistito] = useState(false);
  
  // Plan Selection Modal state
  const [showPlanSelectionModal, setShowPlanSelectionModal] = useState(false);
  const [planSelectionFeature, setPlanSelectionFeature] = useState(null);

  // useRef per prevenire loop - più affidabile di useState
  const initRef = useRef(false);
  const supabaseRef = useRef(supabase);
  const realtimeSubscriptionRef = useRef(null);

  // ========== ABBONAMENTO DAL MENU DELLA LANDING ==========
  useEffect(() => {
    if (!localFamiglia?.id || !planData || !consumePortalRequest()) return;
    const goToPrezzi = () => navigate('/', { state: { section: 'prezzi' } });
    if (!planData.checkFailed && !isPaidPlan(planData.plan)) {
      goToPrezzi();
      return;
    }
    openStripePortal(localFamiglia.id, { onNoCustomer: goToPrezzi });
  }, [localFamiglia?.id, planData, navigate]);

  // ========== ONESIGNAL EXTERNAL ID ==========
  // Set the family ID as OneSignal external_id for better targeting
  useEffect(() => {
    if (!localFamiglia?.id) return;
    
    try {
      if (window.OneSignalDeferred) {
        window.OneSignalDeferred.push(async function(OneSignal) {
          try {
            await OneSignal.login(localFamiglia.id);
            console.log('[OneSignal] Logged in with external_id:', localFamiglia.id);
          } catch (e) {
            console.log('[OneSignal] Login error:', e.message);
          }
        });
      }
    } catch (e) {
      console.log('[OneSignal] Deferred push error:', e.message);
    }
  }, [localFamiglia?.id]);

  // ========== SUPABASE REAL-TIME SUBSCRIPTION ==========
  // Aggiorna automaticamente il feed quando l'assistente registra attività
  useEffect(() => {
    if (!localFamiglia?.id || !supabaseRef.current) return;

    console.log('[Dashboard] Setting up real-time subscription for famiglia:', localFamiglia.id);
    
    // Rimuovi subscription esistente se presente
    if (realtimeSubscriptionRef.current) {
      realtimeSubscriptionRef.current.unsubscribe();
    }

    // Crea nuova subscription per la tabella logs
    const channel = supabaseRef.current
      .channel(`logs_realtime_${localFamiglia.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'logs',
          filter: `famiglia_id=eq.${localFamiglia.id}`
        },
        (payload) => {
          console.log('[Dashboard] Real-time: Nuova attività ricevuta!', payload.new);
          
          // Trasforma il payload nel formato timeline
          const newActivity = {
            id: payload.new.id,
            type: payload.new.attivita,
            detail: payload.new.dettaglio,
            timestamp: payload.new.created_at,
            medicine_name: payload.new.nome_medicina,
            note_text: payload.new.nota_testo,
            photo_url: payload.new.foto_url,
            whatsapp_sent: payload.new.whatsapp_inviato,
            // Nuovi campi per stato esteso
            status_emoji: payload.new.status_emoji,
            status_label: payload.new.status_label,
            status_categoria: payload.new.status_categoria
          };

          // Aggiungi in testa alla timeline
          setTimeline(prev => [newActivity, ...prev]);

          // Aggiorna anche il summary count
          setSummary(prev => prev ? {
            ...prev,
            today_count: (prev.today_count || 0) + 1,
            total_count: (prev.total_count || 0) + 1
          } : prev);

          // Se ha foto, aggiornala anche nella gallery
          if (payload.new.foto_url) {
            setPhotos(prev => [{
              id: payload.new.id,
              url: payload.new.foto_url,
              meal_type: payload.new.dettaglio,
              timestamp: payload.new.created_at
            }, ...prev]);
          }

          // Toast di notifica per il real-time
          toast.success('🔄 Nuova attività registrata!', {
            description: `${newActivity.type === 'mood' ? 'Stato' : newActivity.type}: ${newActivity.detail || 'attività'}`,
            duration: 3000
          });
        }
      )
      .subscribe((status) => {
        console.log('[Dashboard] Real-time subscription status:', status);
      });

    realtimeSubscriptionRef.current = channel;

    // Cleanup on unmount or famiglia change
    return () => {
      console.log('[Dashboard] Cleaning up real-time subscription');
      if (realtimeSubscriptionRef.current) {
        realtimeSubscriptionRef.current.unsubscribe();
        realtimeSubscriptionRef.current = null;
      }
    };
  }, [localFamiglia?.id]);

  // Funzione per caricare stato abbonamento
  const loadSubscriptionStatus = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/user/${userId}/subscription-status`);
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (err) {
      console.error('[Dashboard] Errore loadSubscriptionStatus:', err);
    }
  };

  // Funzione per caricare dati del piano
  const loadPlanData = async (userId) => {
    console.log('[Dashboard] loadPlanData chiamato per userId:', userId);
    try {
      const response = await fetch(`${API_URL}/api/user/${userId}/plan`);
      if (response.ok) {
        const data = await response.json();
        console.log('[Dashboard] Plan data caricato:', data);
        setPlanData(data.trial ? data : { ...data, checkFailed: true });
      } else {
        setPlanData({ checkFailed: true });
      }
    } catch (err) {
      console.error('[Dashboard] Errore loadPlanData:', err);
      setPlanData({ checkFailed: true });
    }
  };

  // Funzione per caricare lista assistiti
  const loadAssistiti = async (famigliaId) => {
    try {
      const response = await fetch(`${API_URL}/api/assistito/famiglia/${famigliaId}`);
      if (response.ok) {
        const data = await response.json();
        setAssistiti(data.assistiti || []);
        console.log('[Dashboard] Assistiti caricati:', data.assistiti?.length);
      }
    } catch (err) {
      console.error('[Dashboard] Errore loadAssistiti:', err);
    }
  };

  // Funzione per aggiungere nuovo assistito
  const handleAddAssistito = async () => {
    if (!newAssistitoName.trim()) {
      toast.error('Inserisci il nome dell\'assistito');
      return;
    }
    
    setAddingAssistito(true);
    try {
      const response = await fetch(`${API_URL}/api/assistito/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          famiglia_id: localFamiglia.id,
          nome_assistito: newAssistitoName.trim()
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Gestisci errore upgrade required
        if (data.detail?.error === 'upgrade_required') {
          toast.error(data.detail.message, {
            description: 'Passa a Premium per monitorare 2 assistiti',
            action: {
              label: 'Upgrade',
              onClick: handleUpgradeToPremium
            }
          });
        } else if (data.detail?.error === 'limit_reached') {
          toast.error(data.detail.message);
        } else {
          toast.error(data.detail?.message || 'Errore nella creazione');
        }
        return;
      }
      
      // Successo!
      toast.success(`${newAssistitoName} aggiunto con successo!`);
      setAssistiti(prev => [...prev, data.assistito]);
      setShowAddAssistitoModal(false);
      setNewAssistitoName('');
      
    } catch (err) {
      console.error('[Dashboard] Errore handleAddAssistito:', err);
      toast.error('Errore di connessione');
    } finally {
      setAddingAssistito(false);
    }
  };

  // Funzione per caricare dati dashboard
  const loadDashboardData = async (famigliaId) => {
    console.log('[Dashboard] loadDashboardData chiamato per famigliaId:', famigliaId);
    try {
      setRefreshing(true);
      
      const [summaryRes, timelineRes, photosRes, moodRes, medicinesRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/${famigliaId}/summary`),
        fetch(`${API_URL}/api/dashboard/${famigliaId}/timeline?days=14`),
        fetch(`${API_URL}/api/dashboard/${famigliaId}/photos?limit=24`),
        fetch(`${API_URL}/api/dashboard/${famigliaId}/mood-stats?days=30`),
        fetch(`${API_URL}/api/dashboard/${famigliaId}/medicines`)
      ]);

      console.log('[Dashboard] API responses:', {
        summary: summaryRes.status,
        timeline: timelineRes.status,
        photos: photosRes.status,
        mood: moodRes.status,
        medicines: medicinesRes.status
      });

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (timelineRes.ok) {
        const data = await timelineRes.json();
        setTimeline(data.timeline || []);
      }
      if (photosRes.ok) {
        const data = await photosRes.json();
        setPhotos(data.photos || []);
      }
      if (moodRes.ok) setMoodStats(await moodRes.json());
      if (medicinesRes.ok) {
        const data = await medicinesRes.json();
        setMedicines(data.medicines || data || []);
      }
      
      console.log('[Dashboard] Dati dashboard caricati con successo');
    } catch (err) {
      console.error('[Dashboard] Errore loadDashboardData:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Effect principale - si esegue UNA SOLA VOLTA quando auth è pronto
  useEffect(() => {
    // Blocco assoluto: se già inizializzato, non fare nulla
    if (initRef.current) {
      console.log('[Dashboard] INIT già eseguito, skip');
      return;
    }
    
    // Se auth sta ancora caricando, aspetta
    if (authLoading) {
      console.log('[Dashboard] Auth in caricamento, aspetto...');
      return;
    }
    
    // Auth caricato, marca come inizializzato SUBITO
    initRef.current = true;
    console.log('[Dashboard] === INIT START ===');
    console.log('[Dashboard] User:', user?.email || 'NESSUNO');
    
    // Se nessun utente, redirect a login
    if (!user) {
      console.log('[Dashboard] Nessun utente, redirect a /login');
      setLoading(false);
      navigate('/login');
      return;
    }
    
    // Funzione async per caricare i dati
    const loadData = async () => {
      try {
        console.log('[Dashboard] Caricamento famiglia per user_id:', user.id);
        console.log('[Dashboard] Supabase client:', supabase ? 'OK' : 'NULL');
        
        // Timeout wrapper per la query
        const queryWithTimeout = async () => {
          return new Promise(async (resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Query timeout dopo 10 secondi'));
            }, 10000);
            
            try {
              const result = await supabase
                .from('famiglie')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(1);
              
              clearTimeout(timeout);
              resolve(result);
            } catch (err) {
              clearTimeout(timeout);
              reject(err);
            }
          });
        };
        
        let data, error;
        try {
          const result = await queryWithTimeout();
          data = result.data;
          error = result.error;
          console.log('[Dashboard] Query completata:', { dataLength: data?.length, error: error?.message });
          
          // Se la query Supabase restituisce 0 risultati (probabilmente RLS blocca), prova via backend
          if (!data || data.length === 0) {
            console.log('[Dashboard] Nessun dato da Supabase (RLS?), provo via backend API...');
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/famiglia/by-user/${user.id}`);
            if (response.ok) {
              const famigliaData = await response.json();
              console.log('[Dashboard] Famiglia da API:', famigliaData?.nome_assistito);
              if (famigliaData && famigliaData.id) {
                setLocalFamiglia(famigliaData);
                
                // Check privacy consent
                if (!famigliaData.privacy_accepted) {
                  console.log('[Dashboard] Privacy non accettata (API fallback 1), mostro modal');
                  setShowPrivacyModal(true);
                }
                
                await Promise.all([
                  loadDashboardData(famigliaData.id),
                  loadPlanData(user.id),
                  loadSubscriptionStatus(user.id),
                  loadAssistiti(famigliaData.id)
                ]);
                setLoading(false);
                return;
              }
            }
          }
        } catch (queryErr) {
          console.error('[Dashboard] Query FALLITA:', queryErr.message);
          // Fallback: prova senza RLS usando il backend
          console.log('[Dashboard] Provo via backend API...');
          try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/famiglia/by-user/${user.id}`);
            if (response.ok) {
              const famigliaData = await response.json();
              console.log('[Dashboard] Famiglia da API:', famigliaData?.nome_assistito);
              if (famigliaData && famigliaData.id) {
                setLocalFamiglia(famigliaData);
                
                // Check privacy consent
                if (!famigliaData.privacy_accepted) {
                  console.log('[Dashboard] Privacy non accettata (API fallback 2), mostro modal');
                  setShowPrivacyModal(true);
                }
                
                await Promise.all([
                  loadDashboardData(famigliaData.id),
                  loadPlanData(user.id),
                  loadSubscriptionStatus(user.id),
                  loadAssistiti(famigliaData.id)
                ]);
                setLoading(false);
                return;
              }
            }
          } catch (apiErr) {
            console.error('[Dashboard] Anche API fallita:', apiErr);
          }
          setLocalFamiglia(null);
          setLoading(false);
          return;
        }
        
        const famigliaData = data && data.length > 0 ? data[0] : null;
        
        if (error) {
          console.error('[Dashboard] Errore query famiglia:', error);
        }
        
        if (!famigliaData) {
          console.log('[Dashboard] Nessuna famiglia trovata per user_id:', user.id);
          setLocalFamiglia(null);
          setLoading(false);
          return;
        }
        
        console.log('[Dashboard] Famiglia trovata:', famigliaData.nome_assistito, 'setup_complete:', famigliaData.setup_complete);
        setLocalFamiglia(famigliaData);
        
        // Check se l'utente ha già accettato la privacy
        if (!famigliaData.privacy_accepted) {
          console.log('[Dashboard] Privacy non ancora accettata, mostro modal');
          setShowPrivacyModal(true);
        }
        
        // Carica dati dashboard in parallelo
        console.log('[Dashboard] Caricamento dati dashboard...');
        await Promise.all([
          loadDashboardData(famigliaData.id),
          loadPlanData(user.id),
          loadSubscriptionStatus(user.id),
          loadAssistiti(famigliaData.id)
        ]);
        
        console.log('[Dashboard] === INIT COMPLETE ===');
        
      } catch (err) {
        console.error('[Dashboard] ERRORE CRITICO:', err);
        setError('Errore nel caricamento dei dati. Riprova più tardi.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [authLoading, user, navigate]); // Dipendenze minime e stabili

  // Auto-refresh quando l'utente torna sulla tab (visibility change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id && localFamiglia) {
        console.log('[Dashboard] Tab tornata visibile, refresh completo...');
        loadPlanData(user.id);
        loadDashboardData(localFamiglia.id); // Refresh anche timeline/attività
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, localFamiglia]);

  // Polling automatico ogni 5 minuti come fallback per real-time (Realtime copre gli inserimenti)
  useEffect(() => {
    if (!localFamiglia?.id) return;
    
    console.log('[Dashboard] Avvio polling automatico (ogni 5 min)');
    
    const pollInterval = setInterval(() => {
      console.log('[Dashboard] Polling: refresh timeline...');
      loadDashboardData(localFamiglia.id);
    }, 300000); // 5 minuti
    
    return () => {
      console.log('[Dashboard] Stop polling automatico');
      clearInterval(pollInterval);
    };
  }, [localFamiglia?.id]);

  const handleLogout = async () => {
    console.log('[Dashboard] Logging out...');
    toast.info('Disconnessione in corso...');
    
    try {
      // Add timeout to prevent hanging
      const logoutPromise = signOut();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Logout timeout')), 3000)
      );
      
      await Promise.race([logoutPromise, timeoutPromise]);
      console.log('[Dashboard] Logged out successfully');
    } catch (error) {
      console.error('[Dashboard] Logout error:', error);
    }
    
    // Clear all local storage related to auth
    localStorage.removeItem('curacheck-auth-token');
    localStorage.removeItem('pwa_auto_login_email');
    
    // Force redirect to landing page
    window.location.href = '/';
  };

  const handleRefresh = () => {
    if (localFamiglia && user) {
      loadDashboardData(localFamiglia.id);
      loadPlanData(user.id);
    }
  };

  const handleUpgradeToPremium = (feature = 'multi-assistito') => {
    // Apri il modal di scelta piano invece di andare direttamente a Stripe
    setPlanSelectionFeature(feature);
    setShowPlanSelectionModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAF9] to-[#E8F5E9] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          {/* Logo */}
          <img 
            src="/logo-curacheck.png" 
            alt="CuraCheck" 
            className="h-16 mx-auto mb-6 animate-pulse"
          />
          
          {/* Spinner animato */}
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-[#E8F5E9] rounded-full"></div>
            <div className="absolute inset-0 border-4 border-[#4A7C59] border-t-transparent rounded-full animate-spin"></div>
          </div>
          
          {/* Messaggi rassicuranti */}
          <h2 className="text-xl font-bold text-[#2C3E50] mb-2">
            Stiamo preparando tutto...
          </h2>
          <p className="text-[#64748B] text-sm mb-4">
            Caricamento del tuo profilo in corso
          </p>
          
          {/* Progress dots animati */}
          <div className="flex justify-center gap-1.5">
            <div className="w-2 h-2 bg-[#4A7C59] rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
            <div className="w-2 h-2 bg-[#4A7C59] rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
            <div className="w-2 h-2 bg-[#4A7C59] rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
          </div>
        </div>
      </div>
    );
  }

  // UI di errore se qualcosa va storto
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-[#2C3E50] mb-2">Errore di Caricamento</h2>
          <p className="text-[#64748B] mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => window.location.reload()}
              className="bg-[#4A7C59] hover:bg-[#3A6345] text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Riprova
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#E5E7EB]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Se l'utente non ha ancora configurato una famiglia, il setup non è completo, o è in modalità edit, mostra Wizard
  // MA solo se non stiamo ancora caricando i dati!
  // IMPORTANTE: Se la famiglia ha già nome_assistito, il setup è considerato completo
  const isSetupComplete = localFamiglia && (localFamiglia.setup_complete || localFamiglia.nome_assistito);
  
  console.log('[Dashboard] Check wizard:', { 
    localFamiglia: localFamiglia?.nome_assistito, 
    setup_complete: localFamiglia?.setup_complete,
    isSetupComplete,
    editMode,
    userId: user?.id,
    loading
  });
  
  // Mostra loading mentre carica i dati della famiglia
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#4A7C59] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Caricamento dashboard...</p>
        </div>
      </div>
    );
  }
  
  // Mostra SetupWizard SOLO se:
  // - Non c'è famiglia (primo accesso)
  // - Setup non è completo (nome_assistito mancante)
  // - È in modalità modifica
  // MA assicurati che user sia disponibile!
  if ((!localFamiglia || !isSetupComplete || editMode) && user?.id) {
    return (
      <SetupWizard 
        user={user}
        famiglia={localFamiglia}
        supabase={supabase}
        isEditMode={editMode}
        onComplete={() => {
          setEditMode(false);
          // Ricarica la pagina per reinizializzare tutto
          window.location.reload();
        }}
        onCancel={editMode ? () => setEditMode(false) : null}
      />
    );
  }

  // Controllo piano fallito: muro neutro con riprova (mai "prova scaduta")
  if (planData?.checkFailed) {
    return <PlanCheckFailedWall onRetry={() => loadPlanData(user?.id)} onLogout={handleLogout} />;
  }

  // Prova scaduta (unica regola backend: trial_end_date passata): solo la scelta del piano
  if (planData?.trial?.expired) {
    return (
      <PlanSelectionModal
        isOpen
        blocking
        onClose={() => {}}
        onLogout={handleLogout}
        currentPlan={planData.plan}
        userId={user?.id}
        famigliaId={localFamiglia?.id}
      />
    );
  }

  // Usa localFamiglia per il rendering
  const famiglia = localFamiglia;

  // Genera Magic Link per QR Code - DEVE portare alla DASHBOARD del figlio (non all'app badante)
  const getMagicLink = () => {
    const baseUrl = window.location.origin;
    // Link alla dashboard del figlio per continuare da mobile
    return `${baseUrl}/dashboard`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Privacy Consent Modal - GDPR compliance */}
      {showPrivacyModal && (
        <PrivacyConsentModal
          user={user}
          famigliaId={famiglia?.id}
          onAccept={() => {
            setShowPrivacyModal(false);
            // Aggiorna lo stato locale
            setLocalFamiglia(prev => prev ? {...prev, privacy_accepted: true} : prev);
          }}
        />
      )}
      
      {/* Desktop Blocker - Mostra QR per forzare installazione su mobile */}
      {/* Appare SOLO se: è desktop E is_pwa_configured è false E non ha bypassato in questa sessione E privacy già accettata */}
      {isDesktopDevice && !famiglia?.is_pwa_configured && !desktopBypassed && !showPrivacyModal && (
        <DesktopBlocker
          magicLink={getMagicLink()}
          familyName={famiglia?.nome_famiglia}
          famigliaId={famiglia?.id}
          isPwaConfigured={famiglia?.is_pwa_configured}
          supabase={supabase}
          onBypass={() => {
            setDesktopBypassed(true);
            // Aggiorna anche lo state locale
            setLocalFamiglia(prev => prev ? {...prev, is_pwa_configured: true} : prev);
          }}
          show={true}
        />
      )}
      
      {/* PWA Install Prompt - shows only if not installed */}
      {showPWAPrompt && (
        <PWAInstallPrompt 
          onDismiss={() => {
            setShowPWAPrompt(false);
            localStorage.setItem('pwa_prompt_dismissed', 'true');
          }}
        />
      )}
      
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        onLogout={handleLogout}
        famiglia={famiglia}
        mobileOpen={mobileMenuOpen}
        setMobileOpen={setMobileMenuOpen}
        subscriptionStatus={subscriptionStatus}
      />

      {/* Main Content */}
      <main className="lg:ml-72">
        {/* Header */}
        <header className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-[#E5E7EB] z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 hover:bg-[#F0F4F1] rounded-xl"
            >
              <Menu className="w-6 h-6 text-[#2C3E50]" />
            </button>
            
            <h1 className="text-lg font-bold text-[#2C3E50] hidden sm:block">
              {activeTab === 'overview' && 'Panoramica'}
              {activeTab === 'timeline' && 'Timeline Attività'}
              {activeTab === 'photos' && 'Galleria Foto'}
              {activeTab === 'link' && 'Link Assistente'}
              {activeTab === 'farmaci' && 'Gestione Farmaci'}
              {activeTab === 'visite' && 'Visite e Specialisti'}
              {activeTab === 'scudo' && 'Scudo Emergenza'}
              {activeTab === 'report' && 'Scarica Report'}
              {activeTab === 'impostazioni' && 'Impostazioni'}
            </h1>

            <Button
              onClick={handleRefresh}
              variant="ghost"
              disabled={refreshing}
              className="rounded-xl"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 lg:p-8 max-w-6xl mx-auto">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Interruttore Notifiche */}
              {localFamiglia && (
                <NotificationToggle 
                  famigliaId={localFamiglia.id}
                  userId={user?.id}
                  nomeAssistito={localFamiglia.nome_assistito}
                />
              )}
              
              {/* Onboarding Steps - Guida Primi Passi */}
              {localFamiglia && (
                <OnboardingSteps
                  famiglia={localFamiglia}
                  hasMedicines={medicines && medicines.length > 0}
                  onGoToMedicines={() => setActiveTab('farmaci')}
                  onGoToLink={() => setActiveTab('link')}
                />
              )}
              
              {/* Notification Status Card (for trial/standard users) */}
              <NotificationStatusCard 
                planData={planData} 
                onUpgrade={handleUpgradeToPremium}
              />

              {/* PWA Install Banner - show if not installed */}
              {!window.matchMedia('(display-mode: standalone)').matches && !window.navigator.standalone && (
                <div 
                  className="bg-gradient-to-r from-[#1B4332] to-[#40916C] rounded-2xl p-4 text-white cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setShowPWAPrompt(true)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Bell className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">Attiva le Notifiche Push</h3>
                      <p className="text-sm text-white/80">Installa l'app per ricevere aggiornamenti in tempo reale</p>
                    </div>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </div>
              )}
              
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatsCard 
                  icon={Sun} 
                  label="Oggi" 
                  value={summary?.today_count || 0}
                  subtext="attività registrate"
                  gradient="bg-gradient-to-br from-[#4A7C59] to-[#5DADE2]"
                />
                <StatsCard 
                  icon={Flame} 
                  label="Streak" 
                  value={`${summary?.streak_days || 0}gg`}
                  subtext="consecutivi"
                  gradient="bg-gradient-to-br from-[#F59E0B] to-[#EF4444]"
                />
                <StatsCard 
                  icon={Activity} 
                  label="Totale" 
                  value={summary?.total_count || 0}
                  subtext="attività"
                  gradient="bg-gradient-to-br from-[#8B5CF6] to-[#EC4899]"
                />
                <StatsCard 
                  icon={Camera} 
                  label="Foto" 
                  value={photos.length}
                  subtext="caricate"
                  gradient="bg-gradient-to-br from-[#06B6D4] to-[#3B82F6]"
                />
              </div>

              {/* Stazione Digitale Card - inclusa nel piano Premium, altrimenti acquisto €3,90 */}
              {localFamiglia && (
                <StazioneDigitaleCard
                  famigliaId={localFamiglia.id}
                  hasStazione={localFamiglia.has_stazione_digitale}
                  familyCode={localFamiglia.family_code}
                  familyToken={localFamiglia.token}
                  isPremium={planData?.plan === 'premium'}
                  medicines={medicines}
                  onNavigateToMedicines={() => setActiveTab('farmaci')}
                  onPurchase={(data) => {
                    setLocalFamiglia(prev => ({
                      ...prev,
                      has_stazione_digitale: true,
                      family_code: data.family_code
                    }));
                  }}
                />
              )}
              
              {/* Passeggiate - Premium; card bloccata per gli altri piani */}
              {planData?.plan === 'premium' ? (
                <WalkStatusCard famiglia={localFamiglia} />
              ) : (
                <LockedGPSCard 
                  userId={user?.id}
                  famigliaId={localFamiglia?.id}
                />
              )}

              {/* Recent Activity & Mood Summary */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Recent Timeline */}
                <div className="bg-white rounded-2xl p-6 border border-[#E5E7EB]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-[#2C3E50]">Ultime attività</h3>
                    <Button variant="ghost" onClick={() => setActiveTab('timeline')} className="text-[#4A7C59] text-sm">
                      Vedi tutte
                    </Button>
                  </div>
                  {timeline.length > 0 ? (
                    <div className="space-y-0">
                      {timeline.slice(0, 5).map((item, idx) => (
                        <TimelineItem key={item.id} item={item} isLast={idx === Math.min(4, timeline.length - 1)} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#64748B]">
                      <Clock className="w-10 h-10 mx-auto mb-2 text-[#C8D6C5]" />
                      <p>Nessuna attività ancora</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Photos */}
              {photos.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border border-[#E5E7EB]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-[#2C3E50]">Ultime foto</h3>
                    <Button variant="ghost" onClick={() => setActiveTab('photos')} className="text-[#4A7C59] text-sm">
                      Vedi tutte
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                    {photos.slice(0, 6).map((photo) => (
                      <div key={photo.id} className="aspect-square rounded-xl overflow-hidden">
                        <img src={photo.url.startsWith('http') ? photo.url : `${API_URL}${photo.url}`} alt="Pasto" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="bg-white rounded-2xl p-6 border border-[#E5E7EB]">
              <h2 className="text-xl font-bold text-[#2C3E50] mb-6">Timeline Completa</h2>
              {timeline.length > 0 ? (
                <div className="space-y-0">
                  {timeline.map((item, idx) => (
                    <TimelineItem key={item.id} item={item} isLast={idx === timeline.length - 1} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-[#64748B]">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-[#C8D6C5]" />
                  <p className="text-lg">Nessuna attività registrata</p>
                  <p className="text-sm">Le attività appariranno qui quando l'assistente le registrerà</p>
                </div>
              )}
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="bg-white rounded-2xl p-6 border border-[#E5E7EB]">
              <h2 className="text-xl font-bold text-[#2C3E50] mb-6">Galleria Foto Pasti</h2>
              <PhotoGallery photos={photos} loading={refreshing} />
            </div>
          )}

          {/* Link Tab */}
          {activeTab === 'link' && <LinkTab famiglia={famiglia} medicines={medicines} onNavigateToMedicines={() => setActiveTab('farmaci')} />}

          {/* Medicines Tab */}
          {activeTab === 'farmaci' && (
            <div className="space-y-6">
              {/* Bottone Richiedi Ricetta - Premium Feature */}
              <RichiestaRicettaButton 
                isPremium={['premium'].includes(subscriptionStatus?.plan?.toLowerCase())}
                medicoSettings={famiglia?.medico_settings}
                pazienteNome={famiglia?.nome_assistito}
                pazienteEta={calcolaEta(famiglia?.data_nascita_assistito)}
                famigliaNome={famiglia?.nome_famiglia}
                userEmail={user?.email}
                famigliaId={famiglia?.id}
                medicines={famiglia?.medicines || []}
                onUpgrade={() => {
                  setPlanSelectionFeature('ricetta');
                  setShowPlanSelectionModal(true);
                }}
              />
              
              <MedicinesTab 
                famiglia={famiglia} 
                onMedicinesChange={(updatedMedicines) => {
                  // Aggiorna lo stato medicines del parent quando cambiano
                  setMedicines(updatedMedicines);
                  console.log('[Dashboard] Medicines updated:', updatedMedicines.length);
                }}
              />
            </div>
          )}

          {/* Report Tab */}
          {activeTab === 'report' && <ReportTab famiglia={famiglia} user={user} />}

          {/* Scudo Emergenza Tab */}
          {activeTab === 'scudo' && (
            ['standard', 'trial', 'premium', 'fondatori', 'founder'].includes(subscriptionStatus?.plan?.toLowerCase()) ? (
              <ScudoTab famiglia={famiglia} />
            ) : (
              <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
                <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-2xl font-bold text-[#2C3E50] mb-3">Tasto Scudo Emergenza</h3>
                <p className="text-[#64748B] mb-6 max-w-md mx-auto" data-testid="scudo-upgrade-text">
                  Il Tasto Scudo permette al tuo assistito di inviarti un&apos;<strong>allerta di emergenza immediata</strong> con un semplice triplo click. 
                  Incluso nei piani <strong>{purchasablePlanNames(founder.soldOut)}</strong>.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={() => handleUpgradeToPremium('scudo')}
                    className="bg-gradient-to-r from-[#F59E0B] to-[#EF4444] hover:from-[#D97706] hover:to-[#DC2626] text-white font-semibold px-6 py-3 rounded-xl"
                    data-testid="scudo-upgrade-button"
                  >
                    <Crown className="w-5 h-5 mr-2" />
                    Scegli un piano
                  </Button>
                </div>
                <p className="text-sm text-[#64748B] mt-4" data-testid="upgrade-entry-price">
                  A partire da {entryMonthlyPrice(founder.soldOut)}/mese
                </p>
              </div>
            )
          )}

          {/* Visite e Specialisti Tab */}
          {activeTab === 'visite' && (
            <GestioneVisiteTab famiglia={famiglia} />
          )}

          {/* Settings Tab */}
          {activeTab === 'impostazioni' && (
            <SettingsTab 
              famiglia={famiglia} 
              supabase={supabase}
              onUpdate={() => window.location.reload()}
              user={user}
              onEditConfig={() => setEditMode(true)}
            />
          )}
        </div>
      </main>
      
      {/* Modal Aggiungi Assistito */}
      {showAddAssistitoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#2C3E50]">Aggiungi Assistito</h3>
              <button 
                onClick={() => {
                  setShowAddAssistitoModal(false);
                  setNewAssistitoName('');
                }}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#64748B] mb-2">
                  Nome dell'assistito *
                </label>
                <input
                  type="text"
                  value={newAssistitoName}
                  onChange={(e) => setNewAssistitoName(e.target.value)}
                  placeholder="Es: Nonna Maria"
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
                  data-testid="new-assistito-name-input"
                  autoFocus
                />
              </div>
              
              <div className="bg-[#F0FDF4] rounded-xl p-4 border border-[#4A7C59]/20">
                <div className="flex gap-3">
                  <Heart className="w-5 h-5 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-[#166534] font-medium">
                      Potrai monitorare {newAssistitoName || 'questo assistito'} insieme a {assistiti[0]?.nome_assistito || 'il tuo assistito'}
                    </p>
                    <p className="text-xs text-[#22C55E] mt-1">
                      L'assistente potrà registrare attività per entrambi
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowAddAssistitoModal(false);
                  setNewAssistitoName('');
                }}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Annulla
              </Button>
              <Button
                onClick={handleAddAssistito}
                disabled={addingAssistito || !newAssistitoName.trim()}
                className="flex-1 bg-[#4A7C59] hover:bg-[#3D6A4B] text-white rounded-xl"
                data-testid="confirm-add-assistito-btn"
              >
                {addingAssistito ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Aggiungo...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Plan Selection Modal - per upgrade da trial */}
      <PlanSelectionModal
        isOpen={showPlanSelectionModal}
        onClose={() => setShowPlanSelectionModal(false)}
        currentPlan={subscriptionStatus?.plan || 'trial'}
        userId={user?.id}
        famigliaId={localFamiglia?.id}
        featureRequested={planSelectionFeature}
      />
    </div>
  );
};

export default FamiliaDashboard;
